import { ReactNode } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { cn } from '@utils'
import { useIsCompact } from '@hooks/useDevice'
import { useSessionStore } from '@/stores'
import BottomSheet from '@/components/ui/BottomSheet'
import { getThemeVariables } from '../utils/themeTokens'

interface OmpDrawerProps {
  isOpen: boolean
  onClose: () => void
  title: string
  children: ReactNode
  // Force a centered modal regardless of popup_layout (e.g. short reactivate step).
  forceCentered?: boolean
  closeDisabled?: boolean
  // Covers the whole drawer, not the scrolling body — a busy overlay inside the body
  // is sized to its scroll height, so it drifts out of view on tall content.
  overlay?: ReactNode
  // Width tier for the centered modal (drawer-layout campaigns keep the drawer width).
  size?: OmpDrawerSize
}

export type OmpDrawerSize = 'default' | 'lg' | 'wide'

// Mobile gated in JS — BottomSheet portals out, so CSS can't hide it.
// Desktop honours popup_layout: 'center' → centered modal, 'right' → right drawer.
const OmpDrawer = ({
  isOpen,
  onClose,
  title,
  children,
  forceCentered,
  closeDisabled,
  overlay,
  size = 'default',
}: OmpDrawerProps) => {
  const requestClose = () => {
    if (!closeDisabled) {
      onClose()
    }
  }
  const isCompact = useIsCompact()
  const campaign = useSessionStore(s => s.campaign)
  const centered = forceCentered || getThemeVariables(campaign)?.popup_layout === 'center'
  // Size tiers only apply to the centered modal; a drawer-layout campaign stays a drawer.
  const sizeClass = centered && size === 'wide' ? 'omp-drawer-wide' : centered && size === 'lg' ? 'omp-drawer-lg' : null

  if (isCompact) {
    return (
      <BottomSheet isOpen={isOpen} onClose={requestClose} title={title} dismissOnBackdrop={false}>
        <div className="omp-drawer-shell">
          <div className="omp-drawer-body">{children}</div>
          {overlay}
        </div>
      </BottomSheet>
    )
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={open => !open && requestClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="omp-drawer-overlay" />
        <Dialog.Content
          className={cn('omp-drawer-content', centered && 'omp-drawer-center', sizeClass)}
          aria-describedby={undefined}
          onInteractOutside={e => e.preventDefault()}
          onPointerDownOutside={e => e.preventDefault()}>
          <div className="omp-drawer-head">
            <Dialog.Title className="omp-drawer-title">{title}</Dialog.Title>
            <button
              type="button"
              className="omp-drawer-close modal-close-btn"
              onClick={requestClose}
              disabled={closeDisabled}
              aria-label="Close">
              <X size={20} strokeWidth={2.25} />
            </button>
          </div>
          <div className="omp-drawer-body">{children}</div>
          {overlay}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export default OmpDrawer
